import React, { useState, useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { PDFDownloadLink } from "@react-pdf/renderer";
import EmployeeReportPDF from "./employeereportpdf";
import EmployeeSummaryReportPDF from "./EmployeeSummaryReportPDF";
import dayjs from "dayjs";
import { hasApprovedHours, reportDaySeconds, unverifiedDayCount } from "./reportHours";

/* ── small icon components to avoid external deps ── */
const IconWeekly = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <line x1="8" y1="14" x2="8" y2="14" strokeWidth="3" strokeLinecap="round" />
    <line x1="12" y1="14" x2="12" y2="14" strokeWidth="3" strokeLinecap="round" />
    <line x1="16" y1="14" x2="16" y2="14" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const IconSummary = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

/* ── reusable PDF download button ── */
const PdfButton = ({ document, fileName, label, icon, colorClass }) => (
  <PDFDownloadLink document={document} fileName={fileName}>
    {({ loading }) => (
      <button
        className={`px-4 py-2 rounded flex items-center gap-2 text-white text-sm font-medium transition-opacity ${
          loading ? "opacity-70 cursor-not-allowed" : "hover:opacity-90"
        } ${colorClass}`}
      >
        {loading ? (
          <>
            <CircularProgress size={13} sx={{ color: "white" }} />
            Generating…
          </>
        ) : (
          <>
            {icon}
            {label}
          </>
        )}
      </button>
    )}
  </PDFDownloadLink>
);

/* ── main modal ── */
const GenerateReportModal = ({ employees, dateRange, onClose }) => {
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  /* employees with hours the report counts: approved hours (v2 recap), else Submitted days as before */
  const filteredEmployees = useMemo(
    () =>
      employees.filter((emp) =>
        (emp.recap || []).some((r) => reportDaySeconds(r) > 0)
      ),
    [employees]
  );
  const approvedMode = useMemo(
    () => employees.some((emp) => (emp.recap || []).some(hasApprovedHours)),
    [employees]
  );

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployees.map((e) => e.id));
    }
    setSelectAll(!selectAll);
  };

  const handleToggle = (id) => {
    setSelectedEmployees((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectedData = useMemo(
    () => filteredEmployees.filter((e) => selectedEmployees.includes(e.id)),
    [selectedEmployees, filteredEmployees]
  );

  const formattedRange = {
    startDate: dateRange?.startDate
      ? dayjs(dateRange.startDate).format("MMM DD, YYYY")
      : "",
    endDate: dateRange?.endDate
      ? dayjs(dateRange.endDate).format("MMM DD, YYYY")
      : "",
  };

  const hasSelection = selectedEmployees.length > 0;
  const timestamp = Date.now();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">

        {/* ── Header ── */}
        <div className="px-6 py-4 border-b">
          <div className="flex justify-between items-center">
            <div className="text-start">
              <h2 className="text-xl font-semibold text-gray-800">
                Generate Reports
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Select employees to include in the report
              </p>
              {approvedMode && (
                <p className="text-xs text-gray-500 mt-0.5">
                  Approved hours only: an automatic clock-out counts once its correction is approved.
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            >
              ✕
            </button>
          </div>

          {/* date range pill */}
          {(formattedRange.startDate || formattedRange.endDate) && (
            <div className="mt-3 inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs font-medium px-3 py-1.5 rounded-full">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="3" y1="10" x2="21" y2="10" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
              </svg>
              {formattedRange.startDate} — {formattedRange.endDate}
            </div>
          )}
        </div>

        {/* ── Employee list ── */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Select All */}
          <div className="mb-3 pb-3 border-b">
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="w-4 h-4 accent-[#50869A]"
              />
              <span className="font-medium text-sm text-gray-700">
                Select All&nbsp;
                <span className="text-gray-400 font-normal">
                  ({filteredEmployees.length} employees)
                </span>
              </span>
            </label>
          </div>

          <div className="space-y-1.5">
            {filteredEmployees.map((emp) => (
              <label
                key={emp.id}
                className={`flex items-center justify-between border rounded-lg px-4 py-3 cursor-pointer transition-colors ${
                  selectedEmployees.includes(emp.id)
                    ? "border-[#50869A] bg-[#f0f7fa]"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedEmployees.includes(emp.id)}
                    onChange={() => handleToggle(emp.id)}
                    className="w-4 h-4 accent-[#50869A]"
                  />
                  <div>
                    <div className="font-medium text-sm text-gray-800">
                      {emp.name}
                    </div>
                    <div className="text-xs text-gray-400">{emp.role}</div>
                    {unverifiedDayCount(emp.recap) > 0 && (
                      <div className="text-xs text-[#E17954]">
                        {unverifiedDayCount(emp.recap)} day(s) awaiting a clock-out correction (not counted)
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-sm text-gray-500 font-medium">
                  {emp.totalHours} hrs
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t bg-gray-50 rounded-b-xl">

          {/* Report type legend */}
          {/* <div className="mb-3 flex gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#50869A] inline-block" />
              Weekly Breakdown — week-by-week tables
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#1e3a5f] inline-block" />
              Summary Report — combined totals for date range
            </span>
          </div> */}

          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              <span className="font-semibold text-gray-700">
                {selectedEmployees.length}
              </span>{" "}
              selected
            </div>

            <div className="flex gap-2.5">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>

              {/* ── Weekly Breakdown PDF ── */}
            {/*  {hasSelection ? (
                <PdfButton
                  document={
                    <EmployeeReportPDF
                      employees={selectedData}
                      dateRange={formattedRange}
                    />
                  }
                  fileName={`Weekly_Report_${timestamp}.pdf`}
                  label="Weekly Report"
                  icon={<IconWeekly />}
                  colorClass="bg-[#50869A]"
                />
              ) : (
                <button
                  disabled
                  className="px-4 py-2 bg-gray-200 text-gray-400 rounded text-sm flex items-center gap-1.5 cursor-not-allowed"
                >
                  <IconWeekly />
                  Weekly Report
                </button>
              )}*/}

              {/* ── Summary (Combined) PDF ── */}
              {hasSelection ? (
                <PdfButton
                  document={
                    <EmployeeSummaryReportPDF
                      employees={selectedData}
                      dateRange={{
                        startDate: dateRange?.startDate,
                        endDate: dateRange?.endDate,
                      }}
                    />
                  }
                  fileName={`Summary_Report_${timestamp}.pdf`}
                  label="Summary Report"
                  icon={<IconSummary />}
                  colorClass="bg-[#1e3a5f]"
                />
              ) : (
                <button
                  disabled
                  className="px-4 py-2 bg-gray-200 text-gray-400 rounded text-sm flex items-center gap-1.5 cursor-not-allowed"
                >
                  <IconSummary />
                  Summary Report
                </button>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default GenerateReportModal;
